import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cln from 'classnames'
import { useTranslation } from 'react-i18next'

import { TARGETING_MODES } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import { mergePlatformCountries } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/PostalCodes/postalCodesReducer'

import allCountries from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/countries.json'
import { locationResultKey } from 'domain/targeting/mappers'
import { isObjectEmpty } from 'utils/helpers'

const Layout = ({ children, borderless = false }) => {
  const { t } = useTranslation()

  return (
    <div className="recommendation__details__running__section recommendation__details__running__section--targeting">
      <p className="recommendation__details__running__section__title">
        {t('Recommendations.campaign.published.geoTargeting')}
      </p>
      <p
        className={cln('recommendation__details__running__section__body', {
          borderless,
        })}
      >
        {children}
      </p>
    </div>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  borderless: PropTypes.bool,
}

export default function Targeting({
  tc,
  tc_run_id,
  targeting,
  isGoogleDisplay = false,
  campaignTargetingGoogle = null,
  isMetaGoogleCombined = false,
  campaignTargetingFb = null,
}) {
  const { t } = useTranslation()

  // A gated platform's own `fb`/`google` GEO fields are stripped at save
  // time (see Context/util.js's extractCampaignWideTargeting) once
  // campaign_targeting is the geo source — Google Display, or Meta+Google
  // combined — so campaignTargetingGoogle/campaignTargetingFb are the only
  // place that GEO data is still visible, identically for every TC. This
  // component only ever renders geo, so audience/age surviving on the TC's
  // own (ungated-for-audience) entry is irrelevant here.
  //
  // The two platforms' geo is then merged into ONE deduped list rather than
  // rendered as two labelled blocks: no platform distinction is wanted in this
  // view (matching the pre-publish LocationsSummary model), and for a gated
  // Meta+Display TC both sides are the same campaign-wide value anyway, so
  // labelling them would show the identical locations twice.
  const mergedTargeting = useMemo(() => {
    const entry = targeting?.[0]
    const fbData = isMetaGoogleCombined ? campaignTargetingFb : entry?.fb
    const googleData = isGoogleDisplay ? campaignTargetingGoogle : entry?.google
    return mergeGeoAcrossPlatforms(fbData, googleData)
  }, [targeting, isGoogleDisplay, campaignTargetingGoogle, isMetaGoogleCombined, campaignTargetingFb])

  if (!mergedTargeting) {
    return <Layout>{t(`Recommendations.geoTargeting.options.${TARGETING_MODES.fdTargeting}`)}</Layout>
  }

  return <TargetingBlock data={mergedTargeting} tc={tc} tc_run_id={tc_run_id} />
}

// Dedupes on locationResultKey — the same identity the editable modal and
// mergePlatformCountries use, so a location picked on both platforms (whose
// per-platform native ids are different id spaces for the same place) counts
// once, not twice.
function dedupeLocations(fbEntries = [], googleEntries = []) {
  const byIdentity = new Map()
  for (const entry of [...(fbEntries || []), ...(googleEntries || [])]) {
    if (!entry?.location) continue
    const identity = locationResultKey(entry.location)
    if (!byIdentity.has(identity)) byIdentity.set(identity, entry)
  }
  return Array.from(byIdentity.values())
}

export function mergeGeoAcrossPlatforms(fbData, googleData) {
  if (!fbData && !googleData) return null

  return {
    // Both sides are written together by one shared Location control, so their
    // modes agree; take whichever is present.
    targeting_mode: fbData?.targeting_mode ?? googleData?.targeting_mode,
    postal_codes: {
      countries: mergePlatformCountries(
        fbData?.postal_codes?.countries,
        googleData?.postal_codes?.countries,
      ),
    },
    range_locations: {
      included: dedupeLocations(fbData?.range_locations?.included, googleData?.range_locations?.included),
      excluded: dedupeLocations(fbData?.range_locations?.excluded, googleData?.range_locations?.excluded),
    },
  }
}

Targeting.propTypes = {
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  targeting: PropTypes.array,
  isGoogleDisplay: PropTypes.bool,
  campaignTargetingGoogle: PropTypes.object,
  isMetaGoogleCombined: PropTypes.bool,
  campaignTargetingFb: PropTypes.object,
}

function TargetingBlock({ data, tc, tc_run_id }) {
  const { t } = useTranslation()

  const _targeting = useMemo(
    () => ({
      targetingMode: data?.targeting_mode ?? TARGETING_MODES.fdTargeting,
      postalCodes: data?.postal_codes,
      rangeLocations: data?.range_locations,
      tc,
      tc_run_id,
    }),
    [data, tc, tc_run_id],
  )

  if (_targeting.targetingMode === TARGETING_MODES.fdTargeting) {
    return (
      <Layout targetingMode={_targeting.targetingMode}>
        {t(`Recommendations.geoTargeting.options.${_targeting.targetingMode}`)}
      </Layout>
    )
  }

  if (_targeting.targetingMode === TARGETING_MODES.postalCodes) {
    const countries = _targeting.postalCodes?.countries

    if (isObjectEmpty(countries)) {
      return (
        <Layout targetingMode={_targeting.targetingMode}>
          {t('Recommendations.campaign.published.noPostalCodesAdded')}
        </Layout>
      )
    }

    const mappedCountries = Object.keys(countries).map((countryKey) => {
      const country = allCountries[countryKey]

      return {
        name: country.name,
        key: countryKey,
        totalPostalCodes: countries[countryKey].length,
      }
    })

    return (
      <Layout borderless>
        {mappedCountries.map((country) => (
          <div key={country.key} className="postal-codes__country">
            <div className="postal-codes__country__container">
              {t('Recommendations.campaign.published.geoTargetingCountry', {
                country: country.name,
                countPostalCodes: country.totalPostalCodes,
              })}
            </div>
          </div>
        ))}
      </Layout>
    )
  }

  if (_targeting.targetingMode === TARGETING_MODES.rangeLocations) {
    const { included = [], excluded = [] } = _targeting.rangeLocations ?? {}

    if ((!included || included.length === 0) && (!excluded || excluded.length === 0)) {
      return (
        <Layout targetingMode={_targeting.targetingMode}>
          {t('Recommendations.campaign.published.noRangeLocationsAdded')}
        </Layout>
      )
    }

    // Group locations by country/country group/region
    const groupKey = (loc) => {
      const l = loc.location
      // Always group by country if possible
      if (l.country_name) return l.country_name
      if (l.country_code && allCountries[l.country_code]) return allCountries[l.country_code].name
      if (l.is_worldwide) return l.name || 'Worldwide'
      if (l.type === 'country_group') return l.name
      if (l.region) return l.region
      return l.name || l.key
    }

    // Group and count types
    const allLocations = [...included, ...excluded]
    const grouped = {}
    allLocations.forEach((loc) => {
      const key = groupKey(loc)
      if (!grouped[key]) grouped[key] = {}
      const type = loc.location.type
      if (!grouped[key][type]) grouped[key][type] = 0
      grouped[key][type]++
    })

    // Render summary for each group
    return (
      <Layout borderless>
        {Object.entries(grouped).map(([group, typeCounts], idx) => {
          // Build summary string for this group
          const summaryParts = Object.entries(typeCounts).map(([type, count]) => {
            // Use translation keys for singular/plural
            const pluralKey = `Recommendations.geoTargeting.rangeLocations.locationTypes.${type}_plural`
            const singularKey = `Recommendations.geoTargeting.rangeLocations.locationTypes.${type}`
            // If plural translation exists and count > 1, use plural, else singular
            const label = (count > 1 && t(pluralKey, { defaultValue: null }))
              ? t(pluralKey)
              : t(singularKey)
            if ((type === 'country' || type === 'country_group')) {
              return label
            }
            return `${count} ${label}`
          })
          return (
            <div key={group + idx} className="range-locations__entry">
              <div className="range-locations__country">
                <div className="range-locations__country__container">
                  {group} – {summaryParts.join(', ')}
                </div>
              </div>
            </div>
          )
        })}
      </Layout>
    )
  }

  return null
}

TargetingBlock.propTypes = {
  data: PropTypes.object,
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
}
